import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Pravesh Nirgam Utara - Enrollment Register
 * 17-row landscape form matching the actual printed format
 */
export const NirgamUtaraTemplate = ({ data }) => {
    const { student, details } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
        const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            return `${match[3]}/${match[2]}/${match[1]}`;
        }
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return dateStr;
            const d = String(date.getDate()).padStart(2, '0');
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const y = date.getFullYear();
            return `${d}/${m}/${y}`;
        } catch (e) {
            return dateStr;
        }
    };

    const rows = [
        { num: 1, label: 'Admission Number', value: student?.admissionNumber || '' },
        { num: 2, label: 'Student Full Name\n(With Surname)', value: student?.name || '' },
        { num: 3, label: 'Father / Mother Name\nName Of Parents If Father Is Not Alive', value: `${student?.fatherName || ''}\n${student?.motherName || ''}` },
        { num: 4, label: 'Caste', value: details?.caste || '' },
        { num: 5, label: 'Occupation Of Father/Parents', value: details?.fatherOccupation || '' },
        { num: 6, label: 'Mother Tongue', value: details?.motherTongue || 'Marathi' },
        { num: 7, label: 'Admission Date', value: formatDate(details?.dateOfAdmission) },
        { num: 8, label: 'In Numbers', value: formatDate(details?.dateOfBirth), group: 'Date Of Birth' },
        { num: 9, label: 'In Letters', value: details?.dateOfBirthInWords || '', group: 'Date Of Birth' },
        { num: 10, label: 'Class At The Time Of Admission', value: details?.classAtAdmission || '' },
        { num: 11, label: 'Previous School Name', value: details?.lastSchool || '' },
        { num: 12, label: 'Previous School Standard', value: details?.lastSchoolClass || '' },
        { num: 13, label: 'Signature Of The Admitting Officer', value: '' },
        { num: 14, label: 'Student Identification Mark\nBriefly Note Two Marks', value: details?.identificationMarks || '' },
        { num: 15, label: 'Exit Time Entry\nTake The Signature Of This Officer', value: '' },
        { num: 16, label: 'Signature Of Principal', value: '' },
        { num: 17, label: 'Description', value: details?.remarks || '' },
    ];

    return (
        <Box sx={{
            fontFamily: '"Times New Roman", Georgia, serif',
            fontSize: '12px',
            lineHeight: 1.4,
            color: '#000',
            backgroundColor: '#fff',
            p: '10mm',
            width: '297mm',
            minHeight: '210mm',
            boxSizing: 'border-box',
            '@media print': {
                width: '297mm',
                margin: 0,
                padding: '10mm',
                '@page': { size: 'landscape' }
            }
        }}>
            <Box sx={{ display: 'flex', height: '100%' }}>
                {/* Main Table */}
                <Box sx={{ flex: 1 }}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        border: '2px solid #000',
                        height: '100%'
                    }}>
                        <tbody>
                            {rows.map((row, idx) => {
                                const isDateGroup = row.group === 'Date Of Birth';
                                return (
                                    <tr key={row.num} style={{ borderBottom: '1px solid #000' }}>
                                        {/* Value Column */}
                                        <td style={{
                                            width: '35%',
                                            padding: '6px 10px',
                                            borderRight: '1px solid #000',
                                            fontSize: 12,
                                            fontWeight: row.value ? 600 : 400,
                                            verticalAlign: 'middle',
                                            whiteSpace: 'pre-line'
                                        }}>
                                            {row.value || ''}
                                        </td>
                                        {/* Sr No Column */}
                                        <td style={{
                                            width: '30px',
                                            padding: '4px',
                                            borderRight: '1px solid #000',
                                            textAlign: 'center',
                                            fontSize: 11,
                                            fontWeight: 600,
                                            verticalAlign: 'middle'
                                        }}>
                                            {row.num}
                                        </td>
                                        {/* Label Column */}
                                        <td style={{
                                            width: '40%',
                                            padding: '6px 10px',
                                            borderRight: isDateGroup ? 'none' : '1px solid #000',
                                            fontSize: 12,
                                            fontWeight: 700,
                                            verticalAlign: 'middle',
                                            whiteSpace: 'pre-line',
                                            textAlign: 'center'
                                        }}>
                                            {row.label}
                                        </td>
                                        {/* Date Of Birth group label spanning rows 8-9 */}
                                        {row.num === 8 && (
                                            <td rowSpan={2} style={{
                                                padding: '6px',
                                                borderLeft: '1px solid #000',
                                                fontSize: 12,
                                                fontWeight: 700,
                                                verticalAlign: 'middle',
                                                textAlign: 'center',
                                                writingMode: 'vertical-rl',
                                                transform: 'rotate(180deg)',
                                                width: '30px'
                                            }}>
                                                Date Of Birth
                                            </td>
                                        )}
                                        {/* Empty cell for non-date-group rows */}
                                        {!isDateGroup && row.num !== 8 && (
                                            <td style={{ display: 'none' }}></td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Box>

                {/* Right Side - School Header (rotated) */}
                <Box sx={{
                    width: 60,
                    borderLeft: '2px solid #000',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <Box sx={{
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        fontSize: 11,
                        fontWeight: 700,
                        px: 0.5
                    }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{
                                fontSize: 10, fontWeight: 700,
                                writingMode: 'vertical-rl', transform: 'rotate(180deg)'
                            }}>
                                Vidya Pratishthan's
                            </Typography>
                            <Typography sx={{
                                fontSize: 13, fontWeight: 700,
                                writingMode: 'vertical-rl', transform: 'rotate(180deg)'
                            }}>
                                Supe English Medium School
                            </Typography>
                            <Typography sx={{
                                fontSize: 8,
                                writingMode: 'vertical-rl', transform: 'rotate(180deg)'
                            }}>
                                A/P- Supe, Tal Baramati, Dist- Pune Pin- 412204
                            </Typography>
                            <Typography sx={{
                                fontSize: 9, fontWeight: 600,
                                writingMode: 'vertical-rl', transform: 'rotate(180deg)',
                                mt: 1
                            }}>
                                Enrollment Register As Student Entry Exit
                            </Typography>
                        </Box>
                    </Box>
                    {/* Sr. No. at bottom */}
                    <Box sx={{
                        position: 'absolute', bottom: 5, right: 5,
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)',
                        fontSize: 10
                    }}>
                        Sr. No. 1
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default NirgamUtaraTemplate;
